/**
 * The wire contract of the SYN scenario-knowledge read plane.
 *
 * WHAT THIS MIRRORS
 *   Two GET routes on the Java host:
 *     /read-models/scenario-knowledge/registry
 *     /read-models/scenario-knowledge/requirements
 *   The mirror is deliberately PARTIAL: it types the fields this surface renders and
 *   keeps the rest as `unknown`. A full mirror would be a second schema that drifts
 *   from the published one, and nothing here needs the fields it does not read.
 *
 * WHAT IT REFUSES TO INVENT
 *   The mirror carries no default, no coercion and no fallback. A document that does
 *   not carry a field this surface renders is a parse failure, not a blank cell: a
 *   blank cell is indistinguishable from "the source said nothing", and those are
 *   different facts about the world.
 */

export const reviewLadder = [
  'SOURCE_IDENTIFIED',
  'CLAUSE_CHECKED',
  'PROFESSIONALLY_REVIEWED',
  'LOCAL_APPLICABILITY_APPROVED',
] as const;
export type ReviewState = (typeof reviewLadder)[number];
export type LadderValue = 'DONE' | 'NOT_DONE';

export const mappingStates = ['MAPPED', 'UNMAPPED', 'UNKNOWN'] as const;
export type MappingState = (typeof mappingStates)[number];

export const evaluationStates = ['CANDIDATE', 'GAP', 'UNKNOWN', 'NOT_EVALUATED'] as const;
export type EvaluationState = (typeof evaluationStates)[number];

export interface ExactPin {
  readonly kind: string;
  readonly id: string;
  readonly version: string;
  readonly schemaVersion: string;
  readonly canonicalizationVersion: string;
  readonly digest: string;
}

export interface KnowledgeSource {
  readonly sourceId: string;
  readonly title: string;
  readonly publisher: string;
  readonly jurisdiction: string;
  readonly sourceType: string;
  readonly edition: string;
  readonly editionStatus: string;
  readonly officialUrl: string;
  readonly urlCaptureNote: string | null;
  readonly locator: {
    readonly state: string;
    readonly reference: string | null;
    readonly scope: string;
  };
  readonly intendedUse: string;
  readonly limitation: string;
  readonly copyrightPolicy: string;
  readonly fullTextStored: boolean;
  readonly originalExcerptStored: boolean;
  readonly checkedAt: string;
  readonly review: Readonly<Record<ReviewState, LadderValue>>;
  readonly fireServiceSopAccepted: boolean;
  readonly publisherConfirmedOnOfficialDomain: boolean;
  readonly publisherConfirmationNote: string | null;
  readonly authorityForRealOperations: string;
  readonly requirementBinding: string;
}

export interface KnowledgeClaim {
  readonly claimId: string;
  readonly sourceId: string;
  readonly locatorRef: string;
  readonly paraphrase: string;
  readonly claimStatus: string;
  readonly claimedAuthority: string;
  readonly bindsTo: readonly { readonly kind: string; readonly ref: string }[];
  readonly bindingNature: string;
  readonly requirementBinding: string;
  readonly requiresHumanProfessionalReview: boolean;
  readonly note: string;
}

export interface DefinitionExactRef {
  readonly capabilityId?: string;
  readonly owner?: string;
  readonly expectedDefinitionVersion?: number;
  readonly expectedDefinitionDigest?: string;
  readonly resolvedDefinitionVersion?: number;
  readonly resolvedDefinitionDigest?: string;
  readonly resolvedDefinitionStatus?: string;
  readonly resolvedDefinitionOriginLabel?: string;
  readonly binding: string;
  readonly reason: string;
}

export interface EvaluationEvidenceBlock {
  readonly availability: string;
  readonly conclusion: string;
  readonly boundEvidenceCount: number;
  readonly coverageComplete?: boolean;
  readonly reason?: string;
  readonly detail?: string;
}

export interface RequirementProjection {
  readonly requirementId: string;
  readonly effectCode: string;
  readonly capabilityClassFromMember: string;
  readonly memberRowFieldCompleteness: {
    readonly declaredFields: readonly string[];
    readonly unknownFields: readonly string[];
    readonly declaredCount: number;
    readonly unknownCount: number;
    readonly totalCount: number;
  };
  readonly stageRefs: readonly string[];
  readonly stageBindingState: string;
  readonly hazardRefs: readonly string[];
  readonly hazardBindingState: string;
  readonly sourceClaimRefs: readonly string[];
  readonly sourceState: string;
  readonly predicate: Readonly<Record<string, unknown>>;
  readonly note: string;
  readonly mappingState: MappingState;
  readonly capabilityDefinitionExactRef: DefinitionExactRef;
  readonly evaluationState: EvaluationState;
  readonly evaluationReason: string;
  readonly evaluationEvidence: EvaluationEvidenceBlock;
}

export interface RegistryDocument {
  readonly labels: readonly string[];
  readonly scope: string;
  readonly researchedAt: string;
  readonly sourceRegistry: {
    readonly reviewLadder: { readonly vocabulary: readonly string[]; readonly meaning: string };
    readonly standingLimits: Readonly<Record<string, unknown>>;
    readonly sourceCount: number;
    readonly sources: readonly KnowledgeSource[];
  };
  readonly claimCandidates: {
    readonly claimPolicy: { readonly meaning: string; readonly paraphraseOnly: boolean };
    readonly claimCount: number;
    readonly claims: readonly KnowledgeClaim[];
  };
  readonly derived: Readonly<Record<string, number>>;
  readonly readPlane: Readonly<Record<string, string>>;
}

export interface RequirementsDocument {
  readonly channel: string;
  readonly labels: readonly string[];
  readonly scenarioTemplateExactPin: ExactPin;
  readonly scenarioTemplateLifecycle: string;
  readonly scenarioTemplatePublicationState: string;
  readonly scenarioTemplateUseState: string;
  readonly mappingPolicy: Readonly<Record<string, unknown>>;
  readonly evaluationPolicy: Readonly<Record<string, unknown>>;
  readonly unknownConditionPolicy: Readonly<Record<string, unknown>>;
  readonly testRules: readonly Readonly<Record<string, unknown>>[];
  readonly requirementCount: number;
  readonly mappingCounts: Readonly<Record<string, number>>;
  readonly definitionBindingCounts: Readonly<Record<string, number>>;
  readonly evaluationCounts: Readonly<Record<string, number>>;
  readonly sourceCounts: Readonly<Record<string, number>>;
  readonly requirements: readonly RequirementProjection[];
}

export class KnowledgeWireError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'KnowledgeWireError';
  }
}

function object(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new KnowledgeWireError(path + ' is not an object');
  }
  return value as Record<string, unknown>;
}

function array(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new KnowledgeWireError(path + ' is not an array');
  return value;
}

function text(value: unknown, path: string): string {
  if (typeof value !== 'string' || value === '') {
    throw new KnowledgeWireError(path + ' is not a non-empty string');
  }
  return value;
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') throw new KnowledgeWireError(path + ' is not a boolean');
  return value;
}

function number(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new KnowledgeWireError(path + ' is not a number');
  }
  return value;
}

function strings(value: unknown, path: string): readonly string[] {
  return array(value, path).map((item, index) => text(item, path + '[' + index + ']'));
}

function numbers(value: unknown, path: string): Readonly<Record<string, number>> {
  const source = object(value, path);
  const out: Record<string, number> = {};
  for (const [key, item] of Object.entries(source)) out[key] = number(item, path + '.' + key);
  return out;
}

function pin(value: unknown, path: string): ExactPin {
  const source = object(value, path);
  return {
    kind: text(source.kind, path + '.kind'),
    id: text(source.id, path + '.id'),
    version: text(source.version, path + '.version'),
    schemaVersion: text(source.schemaVersion, path + '.schemaVersion'),
    canonicalizationVersion: text(
      source.canonicalizationVersion,
      path + '.canonicalizationVersion',
    ),
    digest: text(source.digest, path + '.digest'),
  };
}

function ladder(value: unknown, path: string): Readonly<Record<ReviewState, LadderValue>> {
  const source = object(value, path);
  const out = {} as Record<ReviewState, LadderValue>;
  for (const state of reviewLadder) {
    const item = text(source[state], path + '.' + state);
    if (item !== 'DONE' && item !== 'NOT_DONE') {
      throw new KnowledgeWireError(path + '.' + state + ' is not DONE or NOT_DONE');
    }
    out[state] = item;
  }
  return out;
}

export function parseRegistry(value: unknown): RegistryDocument {
  const root = object(value, 'registry');
  const registry = object(root.sourceRegistry, 'registry.sourceRegistry');
  const claims = object(root.claimCandidates, 'registry.claimCandidates');
  const ladderDoc = object(registry.reviewLadder, 'registry.sourceRegistry.reviewLadder');
  return {
    labels: strings(root.labels, 'registry.labels'),
    scope: text(root.scope, 'registry.scope'),
    researchedAt: text(root.researchedAt, 'registry.researchedAt'),
    sourceRegistry: {
      reviewLadder: {
        vocabulary: strings(ladderDoc.vocabulary, 'reviewLadder.vocabulary'),
        meaning: text(ladderDoc.meaning, 'reviewLadder.meaning'),
      },
      standingLimits: object(registry.standingLimits, 'registry.sourceRegistry.standingLimits'),
      sourceCount: number(registry.sourceCount, 'registry.sourceRegistry.sourceCount'),
      sources: array(registry.sources, 'registry.sourceRegistry.sources').map((item, index) => {
        const path = 'registry.sourceRegistry.sources[' + index + ']';
        const row = object(item, path);
        const locator = object(row.locator, path + '.locator');
        const reference = locator.reference;
        if (reference !== null && typeof reference !== 'string') {
          throw new KnowledgeWireError(path + '.locator.reference is not a string or null');
        }
        const captureNote = row.urlCaptureNote;
        if (captureNote !== null && typeof captureNote !== 'string') {
          throw new KnowledgeWireError(path + '.urlCaptureNote is not a string or null');
        }
        return {
          sourceId: text(row.sourceId, path + '.sourceId'),
          title: text(row.title, path + '.title'),
          publisher: text(row.publisher, path + '.publisher'),
          jurisdiction: text(row.jurisdiction, path + '.jurisdiction'),
          sourceType: text(row.sourceType, path + '.sourceType'),
          edition: text(row.edition, path + '.edition'),
          editionStatus: text(row.editionStatus, path + '.editionStatus'),
          officialUrl: text(row.officialUrl, path + '.officialUrl'),
          urlCaptureNote: captureNote as string | null,
          locator: {
            state: text(locator.state, path + '.locator.state'),
            reference: reference as string | null,
            scope: text(locator.scope, path + '.locator.scope'),
          },
          intendedUse: text(row.intendedUse, path + '.intendedUse'),
          limitation: text(row.limitation, path + '.limitation'),
          copyrightPolicy: text(row.copyrightPolicy, path + '.copyrightPolicy'),
          fullTextStored: bool(row.fullTextStored, path + '.fullTextStored'),
          originalExcerptStored: bool(row.originalExcerptStored, path + '.originalExcerptStored'),
          checkedAt: text(row.checkedAt, path + '.checkedAt'),
          review: ladder(row.review, path + '.review'),
          fireServiceSopAccepted: bool(
            row.fireServiceSopAccepted,
            path + '.fireServiceSopAccepted',
          ),
          publisherConfirmedOnOfficialDomain: bool(
            row.publisherConfirmedOnOfficialDomain,
            path + '.publisherConfirmedOnOfficialDomain',
          ),
          publisherConfirmationNote:
            row.publisherConfirmationNote === null ||
            typeof row.publisherConfirmationNote === 'string'
              ? (row.publisherConfirmationNote as string | null)
              : (() => {
                  throw new KnowledgeWireError(
                    path + '.publisherConfirmationNote is not a string or null',
                  );
                })(),
          authorityForRealOperations: text(
            row.authorityForRealOperations,
            path + '.authorityForRealOperations',
          ),
          requirementBinding: text(row.requirementBinding, path + '.requirementBinding'),
        };
      }),
    },
    claimCandidates: {
      claimPolicy: {
        meaning: text(
          object(claims.claimPolicy, 'claims.claimPolicy').meaning,
          'claims.claimPolicy.meaning',
        ),
        paraphraseOnly: bool(
          object(claims.claimPolicy, 'claims.claimPolicy').paraphraseOnly,
          'claims.claimPolicy.paraphraseOnly',
        ),
      },
      claimCount: number(claims.claimCount, 'claims.claimCount'),
      claims: array(claims.claims, 'claims.claims').map((item, index) => {
        const path = 'claims.claims[' + index + ']';
        const row = object(item, path);
        return {
          claimId: text(row.claimId, path + '.claimId'),
          sourceId: text(row.sourceId, path + '.sourceId'),
          locatorRef: text(row.locatorRef, path + '.locatorRef'),
          paraphrase: text(row.paraphrase, path + '.paraphrase'),
          claimStatus: text(row.claimStatus, path + '.claimStatus'),
          claimedAuthority: text(row.claimedAuthority, path + '.claimedAuthority'),
          bindsTo: array(row.bindsTo, path + '.bindsTo').map((bound, boundIndex) => {
            const boundPath = path + '.bindsTo[' + boundIndex + ']';
            const entry = object(bound, boundPath);
            return {
              kind: text(entry.kind, boundPath + '.kind'),
              ref: text(entry.ref, boundPath + '.ref'),
            };
          }),
          bindingNature: text(row.bindingNature, path + '.bindingNature'),
          requirementBinding: text(row.requirementBinding, path + '.requirementBinding'),
          requiresHumanProfessionalReview: bool(
            row.requiresHumanProfessionalReview,
            path + '.requiresHumanProfessionalReview',
          ),
          note: text(row.note, path + '.note'),
        };
      }),
    },
    derived: numbers(root.derived, 'registry.derived'),
    readPlane: Object.fromEntries(
      Object.entries(object(root.readPlane, 'registry.readPlane')).map(([key, item]) => [
        key,
        text(item, 'registry.readPlane.' + key),
      ]),
    ),
  };
}

export function parseRequirements(value: unknown): RequirementsDocument {
  const root = object(value, 'requirements');
  return {
    channel: text(root.channel, 'requirements.channel'),
    labels: strings(root.labels, 'requirements.labels'),
    scenarioTemplateExactPin: pin(
      root.scenarioTemplateExactPin,
      'requirements.scenarioTemplateExactPin',
    ),
    scenarioTemplateLifecycle: text(
      root.scenarioTemplateLifecycle,
      'requirements.scenarioTemplateLifecycle',
    ),
    scenarioTemplatePublicationState: text(
      root.scenarioTemplatePublicationState,
      'requirements.scenarioTemplatePublicationState',
    ),
    scenarioTemplateUseState: text(
      root.scenarioTemplateUseState,
      'requirements.scenarioTemplateUseState',
    ),
    mappingPolicy: object(root.mappingPolicy, 'requirements.mappingPolicy'),
    evaluationPolicy: object(root.evaluationPolicy, 'requirements.evaluationPolicy'),
    unknownConditionPolicy: object(
      root.unknownConditionPolicy,
      'requirements.unknownConditionPolicy',
    ),
    testRules: array(root.testRules, 'requirements.testRules').map((item, index) =>
      object(item, 'requirements.testRules[' + index + ']'),
    ),
    requirementCount: number(root.requirementCount, 'requirements.requirementCount'),
    mappingCounts: numbers(root.mappingCounts, 'requirements.mappingCounts'),
    definitionBindingCounts: numbers(
      root.definitionBindingCounts,
      'requirements.definitionBindingCounts',
    ),
    evaluationCounts: numbers(root.evaluationCounts, 'requirements.evaluationCounts'),
    sourceCounts: numbers(root.sourceCounts, 'requirements.sourceCounts'),
    requirements: array(root.requirements, 'requirements.requirements').map((item, index) => {
      const path = 'requirements.requirements[' + index + ']';
      const row = object(item, path);
      const mappingState = text(row.mappingState, path + '.mappingState');
      if (!(mappingStates as readonly string[]).includes(mappingState)) {
        throw new KnowledgeWireError(path + '.mappingState is not a known state');
      }
      const evaluationState = text(row.evaluationState, path + '.evaluationState');
      if (!(evaluationStates as readonly string[]).includes(evaluationState)) {
        throw new KnowledgeWireError(path + '.evaluationState is not a known state');
      }
      const ref = object(row.capabilityDefinitionExactRef, path + '.capabilityDefinitionExactRef');
      const evidence = object(row.evaluationEvidence, path + '.evaluationEvidence');
      const completeness = object(
        row.memberRowFieldCompleteness,
        path + '.memberRowFieldCompleteness',
      );
      const optionalText = (value: unknown): string | undefined =>
        typeof value === 'string' ? value : undefined;
      const optionalNumber = (value: unknown): number | undefined =>
        typeof value === 'number' ? value : undefined;
      /** Absent means absent: the key is omitted rather than written as `undefined`. */
      const present = <K extends string, V>(key: K, value: V | undefined): Partial<Record<K, V>> =>
        value === undefined ? {} : ({ [key]: value } as Record<K, V>);
      return {
        requirementId: text(row.requirementId, path + '.requirementId'),
        effectCode: text(row.effectCode, path + '.effectCode'),
        capabilityClassFromMember: text(
          row.capabilityClassFromMember,
          path + '.capabilityClassFromMember',
        ),
        memberRowFieldCompleteness: {
          declaredFields: strings(completeness.declaredFields, path + '.declaredFields'),
          unknownFields: strings(completeness.unknownFields, path + '.unknownFields'),
          declaredCount: number(completeness.declaredCount, path + '.declaredCount'),
          unknownCount: number(completeness.unknownCount, path + '.unknownCount'),
          totalCount: number(completeness.totalCount, path + '.totalCount'),
        },
        stageRefs: strings(row.stageRefs, path + '.stageRefs'),
        stageBindingState: text(row.stageBindingState, path + '.stageBindingState'),
        hazardRefs: strings(row.hazardRefs, path + '.hazardRefs'),
        hazardBindingState: text(row.hazardBindingState, path + '.hazardBindingState'),
        sourceClaimRefs: strings(row.sourceClaimRefs, path + '.sourceClaimRefs'),
        sourceState: text(row.sourceState, path + '.sourceState'),
        predicate: object(row.predicate, path + '.predicate'),
        note: text(row.note, path + '.note'),
        mappingState: mappingState as MappingState,
        capabilityDefinitionExactRef: {
          ...present('capabilityId', optionalText(ref.capabilityId)),
          ...present('owner', optionalText(ref.owner)),
          ...present('expectedDefinitionVersion', optionalNumber(ref.expectedDefinitionVersion)),
          ...present('expectedDefinitionDigest', optionalText(ref.expectedDefinitionDigest)),
          ...present('resolvedDefinitionVersion', optionalNumber(ref.resolvedDefinitionVersion)),
          ...present('resolvedDefinitionDigest', optionalText(ref.resolvedDefinitionDigest)),
          ...present('resolvedDefinitionStatus', optionalText(ref.resolvedDefinitionStatus)),
          ...present(
            'resolvedDefinitionOriginLabel',
            optionalText(ref.resolvedDefinitionOriginLabel),
          ),
          binding: text(ref.binding, path + '.capabilityDefinitionExactRef.binding'),
          reason: text(ref.reason, path + '.capabilityDefinitionExactRef.reason'),
        },
        evaluationState: evaluationState as EvaluationState,
        evaluationReason: text(row.evaluationReason, path + '.evaluationReason'),
        evaluationEvidence: {
          availability: text(evidence.availability, path + '.evaluationEvidence.availability'),
          conclusion: text(evidence.conclusion, path + '.evaluationEvidence.conclusion'),
          boundEvidenceCount: number(
            evidence.boundEvidenceCount,
            path + '.evaluationEvidence.boundEvidenceCount',
          ),
          ...present(
            'coverageComplete',
            typeof evidence.coverageComplete === 'boolean' ? evidence.coverageComplete : undefined,
          ),
          ...present('reason', optionalText(evidence.reason)),
          ...present('detail', optionalText(evidence.detail)),
        },
      };
    }),
  };
}
